import { useMemo, useState } from 'react';
import { Form } from 'react-bootstrap';
import { FaChevronDown, FaChevronUp } from 'react-icons/fa';

import provinceCantonData from '../../../data/ecuador_provinces_cantons_labeled.json';
import {
    BASIS_OPTIONS,
    CUSTOMER_OPTIONS,
    GROUP_OPTIONS,
    ORDER_STATUS_LABELS,
    ORDER_STATUS_OPTIONS,
    PAYMENT_GROUP_LABELS,
    RANGE_PRESETS,
    companyLabel,
    hasAdvancedFilters,
    matchingPreset,
} from '../../../utils/salesReportUtils';

const provinces = Object.keys(provinceCantonData);

const cantonsFor = (state) =>
    (provinceCantonData[state] || [])
        .map((row) => Object.keys(row)[0])
        .filter(Boolean);

const SalesReportFilters = ({
    draft,
    companies = [],
    paymentGroups = [],
    activeCompanyRuc = '',
    errors = [],
    forceAdvanced = false,
    onChange,
    onApply,
    onPreset,
    onReset,
    onUseActiveCompany,
}) => {
    const [advancedOpen, setAdvancedOpen] = useState(() => hasAdvancedFilters(draft) || forceAdvanced);
    const [companyQuery, setCompanyQuery] = useState('');
    const showAdvanced = advancedOpen || forceAdvanced;
    const activePreset = matchingPreset(draft.startDate, draft.endDate);
    const cantons = useMemo(() => cantonsFor(draft.state), [draft.state]);
    const visibleCompanies = companies.filter((company) => {
        const haystack = `${company.tradeName || ''} ${company.legalName || ''} ${company.ruc || ''}`.toLowerCase();
        return haystack.includes(companyQuery.trim().toLowerCase());
    });

    const update = (patch) => onChange({ ...draft, ...patch });

    const toggleValue = (key, value) => {
        const current = draft[key] || [];
        const next = current.includes(value) ? current.filter((item) => item !== value) : [...current, value];
        update({ [key]: next });
    };

    return (
        <form
            className="sr-panel"
            onSubmit={(event) => {
                event.preventDefault();
                onApply();
            }}
        >
            <div className="sr-presets" role="group" aria-label="Rangos rápidos">
                {RANGE_PRESETS.map((preset) => (
                    <button
                        key={preset.id}
                        type="button"
                        className={`sr-preset${activePreset === preset.id ? ' is-active' : ''}`}
                        onClick={() => onPreset(preset.range())}
                    >
                        {preset.label}
                    </button>
                ))}
            </div>

            <div className="sr-filter-grid">
                <Form.Group className="sr-field">
                    <Form.Label column={false}>Desde</Form.Label>
                    <Form.Control
                        type="date"
                        value={draft.startDate || ''}
                        max={draft.endDate || undefined}
                        onChange={(event) => update({ startDate: event.target.value })}
                    />
                </Form.Group>
                <Form.Group className="sr-field">
                    <Form.Label column={false}>Hasta</Form.Label>
                    <Form.Control
                        type="date"
                        value={draft.endDate || ''}
                        min={draft.startDate || undefined}
                        onChange={(event) => update({ endDate: event.target.value })}
                    />
                </Form.Group>
                <Form.Group className="sr-field">
                    <Form.Label column={false}>Criterio</Form.Label>
                    <Form.Select
                        aria-label="Criterio de ventas"
                        value={draft.basis}
                        onChange={(event) => {
                            const basis = event.target.value;
                            update({
                                basis,
                                ...(basis === 'paid' && draft.isPaid === 'false' ? { isPaid: '' } : {}),
                                ...(basis === 'delivered' && draft.isDelivered === 'false' ? { isDelivered: '' } : {}),
                                ...(basis === 'invoiced' ? { orderStatus: [] } : {}),
                            });
                        }}
                    >
                        {BASIS_OPTIONS.map((option) => (
                            <option key={option.value} value={option.value}>
                                {option.label}
                            </option>
                        ))}
                    </Form.Select>
                </Form.Group>
                <Form.Group className="sr-field">
                    <Form.Label column={false}>Agrupar serie</Form.Label>
                    <Form.Select
                        aria-label="Agrupar serie"
                        value={draft.groupBy}
                        onChange={(event) => update({ groupBy: event.target.value })}
                    >
                        {GROUP_OPTIONS.map((option) => (
                            <option key={option.value} value={option.value}>
                                {option.label}
                            </option>
                        ))}
                    </Form.Select>
                </Form.Group>

                <div className="sr-field sr-field-wide">
                    <span className="d-block mb-1 sr-hint">Empresas</span>
                    {companies.length <= 1 ? (
                        <p className="sr-note mb-0">
                            {companies.length === 1
                                ? companyLabel(companies[0])
                                : 'Se incluyen las empresas asignadas a tu usuario.'}
                        </p>
                    ) : (
                        <div className="sr-company-box">
                            <div className="d-flex flex-wrap gap-2 mb-2">
                                <Form.Control
                                    className="sr-company-search"
                                    type="search"
                                    value={companyQuery}
                                    placeholder="Buscar empresa"
                                    aria-label="Buscar empresa"
                                    onChange={(event) => setCompanyQuery(event.target.value)}
                                />
                                {activeCompanyRuc && (
                                    <button type="button" className="sr-btn sr-btn-ghost" onClick={onUseActiveCompany}>
                                        Usar empresa activa
                                    </button>
                                )}
                            </div>
                            <div className="sr-company-list">
                                {visibleCompanies.map((company) => (
                                    <label key={company.ruc} className="sr-check">
                                        <input
                                            type="checkbox"
                                            checked={draft.ruc.includes(company.ruc)}
                                            onChange={() => toggleValue('ruc', company.ruc)}
                                        />
                                        <span>
                                            {companyLabel(company)}
                                            {company.isActive === false ? ' (inactiva)' : ''}
                                            <small className="sr-subline">{company.ruc}</small>
                                        </span>
                                    </label>
                                ))}
                                {!visibleCompanies.length && <p className="sr-note mb-0">Ninguna empresa coincide.</p>}
                            </div>
                        </div>
                    )}
                    <p className="sr-hint mt-2 mb-0">
                        Sin empresa marcada se incluyen todas las que puedes ver. La empresa activa del menú no limita este reporte.
                    </p>
                </div>

                <label className="sr-switch sr-switch-field">
                    <input
                        type="checkbox"
                        checked={draft.compare !== false}
                        onChange={(event) => update({ compare: event.target.checked })}
                    />
                    Comparar con el periodo anterior del mismo largo
                </label>
            </div>

            <p className="sr-hint mt-3 mb-0">{BASIS_OPTIONS.find((option) => option.value === draft.basis)?.hint}</p>

            <button
                type="button"
                className="sr-advanced-toggle"
                aria-expanded={showAdvanced}
                onClick={() => setAdvancedOpen((open) => !open)}
            >
                Más filtros
                {showAdvanced ? <FaChevronUp /> : <FaChevronDown />}
            </button>

            {showAdvanced && (
                <div className="sr-advanced">
                    <Form.Group className="sr-field">
                        <Form.Label column={false}>Búsqueda</Form.Label>
                        <Form.Control
                            type="search"
                            value={draft.keyword}
                            maxLength={80}
                            placeholder="Pedido, suborden, pago, factura, cliente o documento"
                            onChange={(event) => update({ keyword: event.target.value })}
                        />
                    </Form.Group>

                    <div className="sr-field">
                        <span>Estado del pedido</span>
                        {draft.basis === 'invoiced' ? (
                            <p className="sr-note mb-0">Este criterio solo incluye pedidos facturados.</p>
                        ) : (
                            <div className="sr-choice-grid">
                                {ORDER_STATUS_OPTIONS.map((status) => (
                                    <label key={status} className="sr-check">
                                        <input
                                            type="checkbox"
                                            checked={draft.orderStatus.includes(status)}
                                            onChange={() => toggleValue('orderStatus', status)}
                                        />
                                        <span>{ORDER_STATUS_LABELS[status]}</span>
                                    </label>
                                ))}
                            </div>
                        )}
                    </div>

                    {!!paymentGroups.length && (
                        <div className="sr-field">
                            <span>{'Método de pago'}</span>
                            <div className="sr-choice-grid">
                                {paymentGroups.map((group) => (
                                    <label key={group.key} className="sr-check">
                                        <input
                                            type="checkbox"
                                            checked={draft.paymentMethod.includes(group.key)}
                                            onChange={() => toggleValue('paymentMethod', group.key)}
                                        />
                                        <span>{PAYMENT_GROUP_LABELS[group.key] || group.key}</span>
                                    </label>
                                ))}
                            </div>
                        </div>
                    )}

                    <div className="sr-filter-grid">
                        <Form.Group className="sr-field">
                            <Form.Label column={false}>Pago</Form.Label>
                            <Form.Select value={draft.isPaid} onChange={(event) => update({ isPaid: event.target.value })}>
                                <option value="">Todos</option>
                                <option value="true">Pagado</option>
                                {draft.basis !== 'paid' && <option value="false">No pagado</option>}
                            </Form.Select>
                        </Form.Group>
                        <Form.Group className="sr-field">
                            <Form.Label column={false}>Entrega</Form.Label>
                            <Form.Select
                                value={draft.isDelivered}
                                onChange={(event) => update({ isDelivered: event.target.value })}
                            >
                                <option value="">Todas</option>
                                <option value="true">Entregado</option>
                                {draft.basis !== 'delivered' && <option value="false">No entregado</option>}
                            </Form.Select>
                        </Form.Group>
                        <Form.Group className="sr-field">
                            <Form.Label column={false}>Finalizado</Form.Label>
                            <Form.Select value={draft.finished} onChange={(event) => update({ finished: event.target.value })}>
                                <option value="">Todos</option>
                                <option value="true">Finalizado</option>
                                <option value="false">No finalizado</option>
                            </Form.Select>
                        </Form.Group>
                        <Form.Group className="sr-field">
                            <Form.Label column={false}>Cliente</Form.Label>
                            <Form.Select
                                value={draft.customerType}
                                onChange={(event) => update({ customerType: event.target.value })}
                            >
                                {CUSTOMER_OPTIONS.map((option) => (
                                    <option key={option.label} value={option.value}>
                                        {option.label}
                                    </option>
                                ))}
                            </Form.Select>
                        </Form.Group>
                        <Form.Group className="sr-field">
                            <Form.Label column={false}>Provincia</Form.Label>
                            <Form.Control
                                list="sr-provinces"
                                value={draft.state}
                                placeholder="Azuay"
                                onChange={(event) => update({ state: event.target.value })}
                            />
                            <datalist id="sr-provinces">
                                {provinces.map((province) => (
                                    <option key={province} value={province} />
                                ))}
                            </datalist>
                        </Form.Group>
                        <Form.Group className="sr-field">
                            <Form.Label column={false}>Ciudad</Form.Label>
                            <Form.Control
                                list="sr-cities"
                                value={draft.city}
                                placeholder="Cuenca"
                                onChange={(event) => update({ city: event.target.value })}
                            />
                            <datalist id="sr-cities">
                                {cantons.map((canton) => (
                                    <option key={canton} value={canton} />
                                ))}
                            </datalist>
                        </Form.Group>
                        <Form.Group className="sr-field">
                            <Form.Label column={false}>Cupón</Form.Label>
                            <Form.Control
                                value={draft.discountCode}
                                placeholder="Código de descuento"
                                onChange={(event) => update({ discountCode: event.target.value })}
                            />
                        </Form.Group>
                        <Form.Group className="sr-field">
                            <Form.Label column={false}>Total mínimo</Form.Label>
                            <Form.Control
                                inputMode="decimal"
                                value={draft.minTotal}
                                placeholder="0.00"
                                onChange={(event) => update({ minTotal: event.target.value })}
                            />
                        </Form.Group>
                        <Form.Group className="sr-field">
                            <Form.Label column={false}>Total máximo</Form.Label>
                            <Form.Control
                                inputMode="decimal"
                                value={draft.maxTotal}
                                placeholder="0.00"
                                onChange={(event) => update({ maxTotal: event.target.value })}
                            />
                        </Form.Group>
                        <Form.Group className="sr-field">
                            <Form.Label column={false}>ID de producto</Form.Label>
                            <Form.Control
                                value={draft.productId}
                                placeholder="Uno o varios, separados por coma"
                                onChange={(event) => update({ productId: event.target.value })}
                            />
                        </Form.Group>
                        <Form.Group className="sr-field">
                            <Form.Label column={false}>Código Contífico</Form.Label>
                            <Form.Control
                                value={draft.contificoCode}
                                placeholder="Código de producto"
                                onChange={(event) => update({ contificoCode: event.target.value })}
                            />
                        </Form.Group>
                    </div>

                    <label className="sr-switch">
                        <input
                            type="checkbox"
                            checked={Boolean(draft.includeCancelled)}
                            onChange={(event) => update({ includeCancelled: event.target.checked })}
                        />
                        Incluir canceladas
                    </label>
                    <label className="sr-switch">
                        <input
                            type="checkbox"
                            checked={Boolean(draft.includeQuotations)}
                            onChange={(event) => update({ includeQuotations: event.target.checked })}
                        />
                        Incluir cotizaciones
                    </label>
                    <p className="sr-hint mb-0">
                        Esas inclusiones aplican cuando no eliges estados. La semana empieza el lunes y la fecha fin cubre el día completo en Ecuador.
                    </p>
                </div>
            )}

            {!!errors.length && (
                <ul className="sr-error-list">
                    {errors.map((error) => (
                        <li key={error}>{error}</li>
                    ))}
                </ul>
            )}

            <div className="sr-actions mt-3">
                <button type="submit" className="sr-btn">
                    Aplicar filtros
                </button>
                <button type="button" className="sr-btn sr-btn-ghost" onClick={onReset}>
                    Restablecer
                </button>
            </div>
        </form>
    );
};

export default SalesReportFilters;
